import { Auth } from "@aws-amplify/auth";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";

const FOUR_EP_PREFIX = "4epf_";
const FOUR_EP_ID_FILTER = "-_id:4epf_*";
const MAX_OR_CLAUSES = 990;

const fourEpMustNotClause = [
  {
    script: {
      script: {
        source:
          "doc['_id'].size() > 0 && doc['_id'].value != null && doc['_id'].value.startsWith(params.prefix)",
        params: { prefix: FOUR_EP_PREFIX },
      },
    },
  },
];

const countOrClauses = (str = "") => (str.match(/\sOR\s/g) || []).length;

const buildLucenePayload = ({
  searchValue,
  filterQuery,
  searchText,
  isSearching,
  queryOptions,
  start,
  size,
  sort,
  sortBy,
  extraIdMatches = [],
}) => {
  const finalQuery = [FOUR_EP_ID_FILTER, searchValue, filterQuery]
    .filter(Boolean)
    .map((q) => `(${q})`)
    .join(" AND ");

  if (countOrClauses(finalQuery) > MAX_OR_CLAUSES) {
    snackbarService.show({
      message: "Too many conditions, please select less options",
      type: "error",
      duration: 5000,
    });
  }

  const fields = isSearching ? queryOptions : undefined;

  const textClause = {
    query_string: {
      query: `*${searchText}*`,
      analyze_wildcard: true,
      default_operator: "AND",
      fields,
    },
  };

  const matchClause = extraIdMatches?.length > 0
    ? {
      bool: {
        should: [textClause, { ids: { values: extraIdMatches } }],
        minimum_should_match: 1,
      },
    }
    : textClause;

  const query = isSearching
    ? {
      bool: {
        must: [
          matchClause,
          ...(finalQuery ? [{ query_string: { query: finalQuery } }] : []),
        ],
        must_not: fourEpMustNotClause,
      },
    }
    : {
      bool: {
        must: [{ query_string: { query: finalQuery || "*" } }],
        must_not: fourEpMustNotClause,
      },
    };

  return {
    from: start,
    size,
    track_total_hits: true,
    query,
    ...(sortBy && {
      sort: [{ [sortBy]: { order: sort } }],
    }),
  };
};

const buildQueryDSLPayload = (
  searchValue,
  start,
  size,
  sort,
  sortBy,
  queryOptions
) => {
  const parsedQuery =
    typeof searchValue === "string" ? JSON.parse(searchValue) : searchValue;

  const queryBody = {
    from: start,
    size,
    track_total_hits: true,
    query: parsedQuery,
  };

  if (sortBy && !queryBody.sort) {
    queryBody.sort = [{ [sortBy]: { order: sort } }];
  }

  if (
    queryOptions?.fields &&
    Array.isArray(queryOptions.fields) &&
    !queryBody._source
  ) {
    queryBody._source = queryOptions.fields;
  }

  return queryBody;
};

const processSearchResults = (data) => {
  const hits = data.hits?.hits || [];
  const count = data.hits?.total?.value || 0;

  const fields = hits.map((item) => ({
    ...item._source,
    id: item._id,
  }));

  return { data: fields, count };
};

const getEmptyResult = () => ({ data: [], count: 0 });

const buildPayloadByParser = ({
  queryParser,
  searchValue,
  filterQuery,
  start,
  size,
  sort,
  sortBy,
  isSearching,
  queryOptions,
  searchText,
  extraIdMatches,
}) => {
  if (queryParser === "lucene") {
    return buildLucenePayload({
      searchValue,
      filterQuery,
      start,
      size,
      sort,
      sortBy,
      isSearching,
      queryOptions,
      searchText,
      extraIdMatches,
    });
  }

  if (queryParser === "queryDsl") {
    return buildQueryDSLPayload(
      searchValue,
      start,
      size,
      sort,
      sortBy,
      queryOptions
    );
  }

  throw new Error(`query parser ${queryParser} is not supported`);
};

export const OpenSearchConfig = async ({
  start = 0,
  size = 10,
  searchValue,
  sort = "asc",
  sortBy,
  filterQuery,
  queryOptions = [],
  isSearching = false,
  searchText = "",
  queryParser = "lucene",
  extraIdMatches = [],
}) => {
  try {
    const payload = buildPayloadByParser({
      queryParser,
      searchValue,
      filterQuery,
      start,
      size,
      sort,
      sortBy,
      isSearching,
      queryOptions,
      searchText,
      extraIdMatches,
    });

    const authorizationid = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();

    const endpoint = `${process.env.REACT_APP_AWS_OPENSEARCH_ENDPOINT}/_search`;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authorizationid}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return getEmptyResult();
    }

    const data = await response.json();
    return processSearchResults(data);
  } catch (error) {
    console.error("API error:", error);
    return getEmptyResult();
  }
};
